import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';

import { finalize } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';

import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { ChangePasswordComponent } from '../../auth/change-password/change-password.component';
import { OverlayLoaderComponent } from '../../common/loader/overlay-loader/overlay-loader.component';
import { BillingDetailsDialogComponent } from '../billing-details-dialog/billing-details-dialog.component';
import { BillingDetails } from '../../../models/billing-details.model';

import { AuthService } from '../../../services/auth/auth.service';
import { UserAccountService } from '../../../services/user-account/user-account.service';
import { ToastrService } from 'ngx-toastr';
import { CreditsLabelPipe } from '../../../pipes/credits-label.pipe';

@Component({
  selector: 'app-user-account',
  standalone: true,
  imports: [CommonModule,
    FormsModule,
    RouterModule,
    HeaderComponent,
    OverlayLoaderComponent,
    SidebarComponent,
    BillingDetailsDialogComponent, CreditsLabelPipe],
  templateUrl: './user-account.component.html',
  styleUrls: ['./user-account.component.css'],
})
export class UserAccountComponent implements OnInit {
  isLoading: boolean = false;
  selectedTab: string = 'account-details';
  isLoadingSubscription: boolean = false;
  isExpiringPlan: boolean = false;
  isActivatingPlan: string | null = null;
  twoFactorEnabled: boolean = false;
  showTwoFactorDialog: boolean = false;
  qrCodeUrl: string = '';
  verificationCode: string = '';
  userData: any = {
    name: '',
    email: '',
    licenseExpiry: '',
    totalCredits: 0,
    remainingCredits: 0,
    availableCredits: 0,
    // perDayCount: 0,
    todayCount: 0,
    licenseName: '',
    roleType: '',
    sourcePermission: '',
    searchPermission: {
      face: false,
      osint: false,
      leak: false,
      virtual: false,
      vehicle: false,
      pan: false,
      gst: false,
      din: false,
      mobile_pan: false,
      phonepe_fastag: false,
      phonepe_upi: false,
      name: false,
    },
  };

  subscriptionData: any = {
    currentPlan: null,
    upcomingPlans: [],
    availableCredits: null,
    availableTotalCredits: null,
  };

  showPlanHistoryModal: boolean = false;
  planHistoryData: any[] = [];
  showPaymentHistoryModal: boolean = false;
  paymentHistoryData: any[] = [];
  showActivatePlanModal: boolean = false;
  planToActivate: any = null;
  hasBillingDetails: boolean = false;
  billingDetails: BillingDetails | null = null;
  showBillingModal: boolean = false;
  pendingInvoiceAction: { transactionId: string; download: boolean } | null = null;
  invoiceBusyId: string | null = null;

  /** Phase 10 hardcoded support contacts (same as contact_us templates). */
  readonly supportEmail = 'contact@ryno.in';
  readonly supportPhone = '+91 8143312026';
  readonly supportPhoneTel = '+918143312026';

  constructor(
    private dialog: MatDialog,
    private authService: AuthService,
    private userAccountService: UserAccountService,
    private toaster: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
  ) { }

  ngOnInit() {
    const tab = this.route.snapshot.queryParamMap.get('tab');
    if (tab === 'subscription' || tab === 'account-details') {
      this.selectedTab = tab;
    }
    this.fetchUserProfile();
    this.fetchSubscriptionDetails();
  }

  fetchUserProfile() {
    this.isLoading = true;

    this.userAccountService.getUserProfile().subscribe({
      next: (response) => {
        const data = response?.data ?? {};
        const subscription = data.subscriptions?.[0] || {};
        this.twoFactorEnabled = data.is_2fa_enabled;
        const isUnlimited = this.userAccountService.pickIsUnlimited(response);
        const remainingCredits = isUnlimited
          ? Number.POSITIVE_INFINITY
          : (subscription.remaining_credits ?? 0);
        const totalCredits = isUnlimited
          ? Number.POSITIVE_INFINITY
          : (subscription.total_credits ?? 0);
        const availableCredits = isUnlimited
          ? Number.POSITIVE_INFINITY
          : (data.available_credits ??
              subscription.available_credits ??
              subscription.remaining_credits ??
              0);

        this.userData = {
          name: data.name || 'N/A',
          email: data.email || 'N/A',
          licenseExpiry: this.formatDate(subscription.expiry_date),
          totalCredits,
          remainingCredits,
          availableCredits,
          // perDayCount: subscription.per_day_count ?? 0,
          todayCount: subscription.today_count ?? 0,
          licenseName: subscription.license_name || 'N/A',
          roleType: data.user_type || 'N/A',
          sourcePermission: data.user_permission || 'N/A',
          searchPermission: {
            face: !!subscription.face_search_permission,
            osint: !!subscription.osint_search_permission,
            leak: !!subscription.leak_search_permission,
            virtual: !!subscription.virtual_number_permission,
            vehicle: !!subscription.vehicle_search_permission,
            pan: !!subscription.pan_search_permission,
            gst: !!subscription.gst_search_permission,
            din: !!subscription.din_search_permission,
            mobile_pan: !!subscription.mobile_pan_search_permission,
            phonepe_fastag: !!subscription.phonepe_fastag_search_permission,
            phonepe_upi: !!subscription.phonepe_upi_search_permission,
            name: !!subscription.name_search_permission,
          },
        };

        this.isLoading = false;
      },
      error: (error) => {
        console.error(error);
        this.isLoading = false;
      },
    });
  }

  onTabChange(tab: string) {
    this.selectedTab = tab;
    if (tab === 'subscription') {
      this.fetchSubscriptionDetails();
    }
  }

  fetchSubscriptionDetails() {
    this.isLoadingSubscription = true;
    this.userAccountService.getSubscriptionDetails().subscribe({
      next: (response) => {
        const data = response?.data ?? {};
        const isUnlimited = this.userAccountService.pickIsUnlimited(response);
        this.subscriptionData.availableCredits = isUnlimited
          ? Number.POSITIVE_INFINITY
          : (data.availableCredits ?? data.available_credits ?? null);
        this.subscriptionData.availableTotalCredits = isUnlimited
          ? Number.POSITIVE_INFINITY
          : (data.availableTotalCredits ?? data.available_total_credits ?? null);

        // Process current plan
        if (data.currentPlan && data.hasActiveSubscription) {
          const currentPlan = data.currentPlan;
          const usedCredits = isUnlimited
            ? 0
            : (data.totalCredits ?? 0) - (data.remainingCredits ?? 0);

          this.subscriptionData.currentPlan = {
            name: isUnlimited
              ? currentPlan.plan_name || currentPlan.license_name || 'Unlimited Searches'
              : currentPlan.plan_name || 'Active Plan',
            usedCredits: usedCredits,
            totalCredits: isUnlimited
              ? Number.POSITIVE_INFINITY
              : (data.totalCredits ?? 0),
            daysLeft: data.remainingValidityDays ?? 0,
            // creditsPerDay: data.dailyUsageLimit ?? 20,
            expiryDate: data.expiryDate,
            startDate: data.startDate,
            status: data.status,
            isExpired: data.isExpired || false,
            creditsExhausted: isUnlimited ? false : (data.creditsExhausted || false),
            remainingCredits: isUnlimited
              ? Number.POSITIVE_INFINITY
              : (data.remainingCredits ?? 0),
            todayUsage: data.todayUsage ?? 0,
            isUnlimited,
          };
        } else {
          this.subscriptionData.currentPlan = null;
        }

        // Process queued/upcoming plans from queues array
        if (
          data.queues &&
          Array.isArray(data.queues) &&
          data.queues.length > 0
        ) {
          this.subscriptionData.upcomingPlans = data.queues
            .map((queueItem: any) => {
              const queuedPlan = queueItem.plan;
              if (queuedPlan) {
                return {
                  name: queuedPlan.plan_name || 'Premium Plan',
                  planId: queuedPlan.plan_id,
                  planCode: queuedPlan.plan_code,
                  description: queuedPlan.description,
                  totalCredits: queueItem.total_credits ?? queuedPlan.total_credits,
                  validityPeriod: queuedPlan.validity_period,
                  // dailyUsageLimit: queuedPlan.max_searches_per_day,
                  subscriptionId: queueItem.subscription_id,
                  status: queueItem.status,
                  createdOn: queueItem.created_on,
                  updatedOn: queueItem.updated_on,
                };
              }
              return null;
            })
            .filter((plan: any) => plan !== null);
        } else {
          this.subscriptionData.upcomingPlans = [];
        }

        // Process plan history from history array
        if (
          data.history &&
          Array.isArray(data.history) &&
          data.history.length > 0
        ) {
          this.planHistoryData = data.history.map((historyItem: any) => {
            const plan = historyItem.plan || {};
            let status = 'Used'; // Default status

            if (historyItem.isExpired) {
              status = 'Expired';
            } else if (historyItem.is_active) {
              status = 'Ongoing';
            } else {
              status = 'Used';
            }

            return {
              planName: plan.plan_name || 'Unknown Plan',
              status: status,
              startDate: this.formatDateForDisplay(historyItem.start_date),
              endDate: this.formatDateForDisplay(historyItem.expiry_date),
              totalCredits: plan.total_credits,
            };
          });

        } else {
          this.planHistoryData = [];
        }

        // Process payment history from payments array
        if (
          data.payments &&
          Array.isArray(data.payments) &&
          data.payments.length > 0
        ) {
          this.paymentHistoryData = data.payments.map((payment: any) => {
            const plan = payment.plan || {};
            const status = payment.status || 'pending';
            const formattedStatus = status.charAt(0).toUpperCase() + status.slice(1);
            const paymentMethod = payment.payment_method || null;

            return {
              transactionId: payment.transaction_id,
              planName: plan.plan_name || 'Unknown Plan',
              amount: `₹${payment.amount || '0.00'}`,
              credits: payment.credits_purchased || 0,
              status: formattedStatus,
              paymentMethod: paymentMethod ? paymentMethod.toUpperCase() : null,
              date: this.formatDateForDisplay(payment.created_on),
              invoiceId: payment.invoice_id || null,
              invoiceNumber: payment.invoice_number || null,
            };
          });
        } else {
          this.paymentHistoryData = [];
        }

        this.hasBillingDetails = Boolean(data.has_billing_details);

        this.isLoadingSubscription = false;
      },
      error: (error) => {
        console.error('Error fetching subscription details:', error);
        this.subscriptionData.currentPlan = null;
        this.subscriptionData.upcomingPlans = [];
        this.subscriptionData.availableCredits = null;
        this.subscriptionData.availableTotalCredits = null;
        this.isLoadingSubscription = false;
      },
    });
  }

  calculateDaysLeft(expiryDate: string): number {
    if (!expiryDate) return 0;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  }

  getCreditUsagePercentage(): number {
    if (!this.subscriptionData?.currentPlan) return 0;
    const { usedCredits, totalCredits } = this.subscriptionData.currentPlan;
    return totalCredits > 0 ? (usedCredits / totalCredits) * 100 : 0;
  }

  formatDate(date: string) {
    if (!date) {
      return 'N/A';
    }
    return new Date(date).toISOString().split('T')[0];
  }

  openChangePasswordDialog() {
    this.dialog.open(ChangePasswordComponent, {
      panelClass: 'change-password-dialog',
      width: '500px',
      data: {},
    });
  }

  onLogOutButtonClick() {
    this.logout();
  }

  onTwoFactorToggle() {
    if (this.twoFactorEnabled) {
      this.showTwoFactorDialog = true;
      this.generateQRCode();
    } else {
      this.userAccountService
        .disable2FA({
          enable: false,
        })
        .subscribe(
          (data) => {
            this.twoFactorEnabled = false;
            this.toaster.success(data.data.message);
          },
          (error) => {
            this.twoFactorEnabled = true;
            this.toaster.error('Unable to disable 2FA');
          },
        );
      console.log('Two-factor authentication disabled');
    }
  }

  generateQRCode() {
    this.userAccountService.get2FaCode().subscribe(
      (data) => {
        this.qrCodeUrl = data.data.qr_code;
      },
      (error) => { },
    );
    console.log('Generating QR code...');
  }

  onCodeInput(event: any) {
    const value = event.target.value;
    this.verificationCode = value.replace(/[^0-9]/g, '');
  }

  isCodeValid(): boolean {
    return this.verificationCode.length === 6;
  }

  onVerifyAndEnable() {
    if (!this.isCodeValid()) {
      return;
    }

    this.userAccountService
      .verifyEnable2Fa({
        token: this.verificationCode,
      })
      .subscribe(
        (data) => {
          this.toaster.success(data.data.message);
          this.twoFactorEnabled = true;
          this.closeTwoFactorDialog();
        },
        (error) => {
          this.toaster.error('Unable to Verify OTP');
        },
      );
  }

  closeTwoFactorDialog() {
    this.showTwoFactorDialog = false;
    this.verificationCode = '';

    if (!this.twoFactorEnabled) {
      this.twoFactorEnabled = false;
    }
  }

  onDialogBackdropClick(event: MouseEvent) {
    this.twoFactorEnabled = false;
    this.closeTwoFactorDialog();
  }

  onAddPlanClick() {
    this.router.navigate(['/plan-pricing']);
  }

  onViewPlanHistoryClick() {
    this.showPlanHistoryModal = true;
  }

  closePlanHistoryModal() {
    this.showPlanHistoryModal = false;
  }

  onViewPaymentHistoryClick() {
    this.showPaymentHistoryModal = true;
  }

  onExpireCurrentPlanClick() {
    if (this.isExpiringPlan) return;
    const confirmed = window.confirm(
      'Are you sure you want to expire your current plan? Your next queued plan may activate automatically.'
    );
    if (!confirmed) return;
    this.isExpiringPlan = true;
    this.userAccountService.expireActiveCredit().subscribe({
      next: (response) => {
        const message = response?.data?.message ?? response?.message ?? 'Current plan expired successfully.';
        this.toaster.success(message);
        this.fetchSubscriptionDetails();
      },
      error: (error) => {
        const message = error?.error?.message ?? error?.message ?? 'Failed to expire current plan.';
        this.toaster.error(message);
      },
      complete: () => {
        this.isExpiringPlan = false;
      },
    });
  }

  onActivatePlanClick(upcomingPlan: any) {
    if (!upcomingPlan?.subscriptionId) {
      this.toaster.error('Subscription ID is missing.');
      return;
    }
    if (this.isActivatingPlan === upcomingPlan.subscriptionId) return;

    this.planToActivate = upcomingPlan;
    this.showActivatePlanModal = true;
  }

  onConfirmActivatePlan() {
    if (!this.planToActivate?.subscriptionId) {
      this.toaster.error('Subscription ID is missing.');
      this.closeActivatePlanModal();
      return;
    }

    this.isActivatingPlan = this.planToActivate.subscriptionId;
    this.userAccountService.activateCredit(this.planToActivate.subscriptionId).subscribe({
      next: (response) => {
        const message = response?.data?.message ?? response?.message ?? 'Plan activated successfully.';
        this.toaster.success(message);
        this.fetchSubscriptionDetails();
        this.fetchUserProfile();
        this.closeActivatePlanModal();
      },
      error: (error) => {
        const message = error?.error?.message ?? error?.message ?? 'Failed to activate plan.';
        this.toaster.error(message);
      },
      complete: () => {
        this.isActivatingPlan = null;
      },
    });
  }

  closeActivatePlanModal() {
    this.showActivatePlanModal = false;
    this.planToActivate = null;
  }

  closePaymentHistoryModal() {
    this.showPaymentHistoryModal = false;
  }

  onUpdateBillingDetailsClick() {
    this.pendingInvoiceAction = null;
    this.showBillingModal = true;
  }

  closeBillingModal() {
    this.showBillingModal = false;
    this.pendingInvoiceAction = null;
  }

  onBillingDetailsSaved(details: BillingDetails) {
    this.hasBillingDetails = true;
    this.billingDetails = details;
    this.showBillingModal = false;
    const pending = this.pendingInvoiceAction;
    this.pendingInvoiceAction = null;
    if (pending?.transactionId) {
      this.fetchInvoice(pending.transactionId, pending.download);
    }
  }

  isCompletedPayment(payment: any): boolean {
    return String(payment?.status || '').toLowerCase() === 'completed';
  }

  onViewInvoice(payment: any, event?: Event) {
    event?.stopPropagation();
    this.requestInvoice(payment, false);
  }

  onDownloadInvoice(payment: any, event?: Event) {
    event?.stopPropagation();
    this.requestInvoice(payment, true);
  }

  private requestInvoice(payment: any, download: boolean) {
    if (!this.isCompletedPayment(payment) || !payment?.transactionId) {
      this.toaster.error('Invoice is available only for successful payments.');
      return;
    }

    if (!this.hasBillingDetails) {
      this.pendingInvoiceAction = {
        transactionId: payment.transactionId,
        download,
      };
      this.showBillingModal = true;
      return;
    }

    this.fetchInvoice(payment.transactionId, download);
  }

  private fetchInvoice(transactionId: string, download: boolean) {
    this.invoiceBusyId = transactionId;
    this.userAccountService.getPaymentInvoice(transactionId, download).subscribe({
      next: (response) => {
        const blob = response.body;
        if (!blob) {
          this.toaster.error('Invoice could not be generated.');
          this.invoiceBusyId = null;
          return;
        }
        const filename = this.userAccountService.getFilenameFromResponse(
          response,
          'invoice.pdf',
        );
        if (download) {
          this.userAccountService.saveBlobFile(blob, filename);
        } else {
          this.userAccountService.openBlobInNewTab(blob);
        }
        this.invoiceBusyId = null;
      },
      error: async (error) => {
        this.invoiceBusyId = null;
        const needsBilling = await this.isBillingRequiredError(error);
        if (needsBilling) {
          this.pendingInvoiceAction = { transactionId, download };
          this.showBillingModal = true;
          return;
        }
        this.toaster.error(
          await this.userAccountService.parseErrorMessage(
            error,
            'Unable to load invoice. Please try again.',
          ),
        );
      },
    });
  }

  private async isBillingRequiredError(error: any): Promise<boolean> {
    if (error?.status === 409) {
      return true;
    }
    const body = error?.error;
    if (body instanceof Blob) {
      try {
        const parsed = JSON.parse(await body.text());
        return Boolean(parsed?.data?.needs_billing_details);
      } catch {
        return false;
      }
    }
    return Boolean(body?.data?.needs_billing_details);
  }

  formatDateForDisplay(dateString: string): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return 'N/A';
    }
  }

  getStatusIconClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'ongoing':
        return 'bi bi-record-circle-fill status-icon-ongoing';
      case 'used':
        return 'bi bi-check-circle-fill status-icon-used';
      case 'expired':
        return 'bi bi-x-circle-fill status-icon-expired';
      default:
        return 'bi bi-circle status-icon-default';
    }
  }

  getPaymentStatusIconClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'bi bi-check-circle-fill status-icon-completed';
      case 'pending':
        return 'bi bi-clock-fill status-icon-pending';
      case 'failed':
        return 'bi bi-x-circle-fill status-icon-failed';
      default:
        return 'bi bi-circle status-icon-default';
    }
  }

  logout() {
    this.isLoading = true;

    this.authService
      .logout()
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.authService.postLogout();
        }),
      )
      .subscribe({
        next: (response) => { },
        error: (error) => {
          console.log(error);
        },
      });
  }
}
